import { useEffect, useMemo, useState } from 'react';
import { fieldDefApi, type RobotFieldDef, type RobotFieldGroup } from './api';

export const SKELETON_KEYS = new Set([
  'ffsn',
  'currentStatus',
  'modelId',
  'skuId',
  'supplierId',
  'customerId',
  'locationId',
]);

export function isSkeletonField(key: string): boolean {
  return SKELETON_KEYS.has(key);
}

export function getFieldLabel(def: RobotFieldDef, locale: string): string {
  if (locale === 'zh' && def.label) return def.label;
  return def.labelEn;
}

export function useFieldDefs() {
  const [fieldDefs, setFieldDefs] = useState<RobotFieldDef[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fieldDefApi
      .list({ enabledOnly: 'true' })
      .then((data) => setFieldDefs(data))
      .catch(() => setFieldDefs([]))
      .finally(() => setLoading(false));
  }, []);

  const fieldsByGroup = useMemo(() => {
    const map: Partial<Record<RobotFieldGroup, RobotFieldDef[]>> = {};
    for (const def of fieldDefs) {
      const g = def.group as RobotFieldGroup;
      if (!map[g]) map[g] = [];
      map[g]!.push(def);
    }
    for (const arr of Object.values(map)) {
      arr!.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return map as Record<RobotFieldGroup, RobotFieldDef[]>;
  }, [fieldDefs]);

  const listFields = useMemo(
    () =>
      fieldDefs
        .filter((d) => d.showInList && !isSkeletonField(d.key))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [fieldDefs],
  );

  return { fieldDefs, fieldsByGroup, listFields, loading };
}
